import React, { createContext, useContext, useState, useEffect } from 'react';
import { useError } from './ErrorContext';

const FollowUpEmailContext = createContext();

// Initial values structure for follow-up email
const initialFollowUpEmailValues = {
    followUpEmailDelayHours: 0,
    followUpEmailDelayMinutes: 0,
    toEmails: {
        newLeadsEmail: '',
        userEmail: '',
    },
    ccRecipients: '',
    bccRecipients: '',
    subject: '',
    message: '',
    attachment: null, // Could be a file object or URL
    autoFollowUpIntroEmail: false, // Add this to track the toggle state
};

export const FollowUpEmailProvider = ({ children }) => {
    // Initialize state from localStorage
    const [followUpEmailState, setFollowUpEmailState] = useState(() => {
        // Try to load the followUpEmailState from localStorage if it exists
        const savedState = localStorage.getItem('followUpEmailState');
        return savedState ? JSON.parse(savedState) : {};
    });
    const [error, setError] = useState(null);

    // Get error context for global error handling
    const { addError } = useError();

    // Enhanced error handling for follow-up email operations
    const handleFollowUpError = (error, operation = 'follow-up-email-operation') => {
        console.error('Follow-up email operation error:', error);

        // Only show user-relevant errors, log technical errors
        if (error.message?.includes('validation') ||
            error.message?.includes('must be') ||
            error.message?.includes('required') ||
            error.message?.includes('permission') ||
            error.message?.includes('session')) {
            addError(error, {
                source: 'follow-up-email',
                type: operation,
                retryable: false,
                autoDismiss: true
            });
        }

        setError(error.message || 'An error occurred while updating follow-up email settings');
    };

    // Clear error after timeout
    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => {
                setError(null);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [error]);

    // Save to localStorage whenever state changes
    useEffect(() => {
        try {
            localStorage.setItem('followUpEmailState', JSON.stringify(followUpEmailState));
        } catch (error) {
            handleFollowUpError(error, 'follow-up-email-storage');
        }
    }, [followUpEmailState]);

    // Function to update follow-up email data for a specific card with error handling
    const updateFollowUpEmailData = (cardId, newData) => {
        try {
            if (!cardId) {
                throw new Error('Card ID is required to update follow-up email data');
            }

            setFollowUpEmailState((prevState) => {
                const newState = {
                    ...prevState,
                    [cardId]: {
                        ...(prevState[cardId] || {}),
                        ...newData,
                    },
                };

                return newState;
            });

            setError(null); // Clear any existing errors on successful update
        } catch (error) {
            handleFollowUpError(error, 'follow-up-email-update');
        }
    };

    // Function to initialize follow-up email settings with default values
    const initializeFollowUpEmail = (cardId) => {
        try {
            if (!cardId) {
                throw new Error('Card ID is required to initialize follow-up email');
            }

            if (!followUpEmailState[cardId]) {
                setFollowUpEmailState((prevState) => {
                    const newState = {
                        ...prevState,
                        [cardId]: initialFollowUpEmailValues,
                    };

                    return newState;
                });
                console.log('Follow-up email initialized for card:', cardId);
            }
        } catch (error) {
            handleFollowUpError(error, 'follow-up-email-initialization');
        }
    };

    // Function to clear error
    const clearError = () => {
        setError(null);
    };

    // Setter functions for various attributes with error handling
    const setFollowUpEmailDelayHours = (cardId, hours) => {
        try {
            if (typeof hours !== 'number' || hours < 0) {
                throw new Error('Delay hours must be a non-negative number');
            }
            updateFollowUpEmailData(cardId, { followUpEmailDelayHours: hours });
        } catch (error) {
            handleFollowUpError(error, 'follow-up-email-delay-hours-update');
        }
    };

    const setFollowUpEmailDelayMinutes = (cardId, minutes) => {
        try {
            if (typeof minutes !== 'number' || minutes < 0) {
                throw new Error('Delay minutes must be a non-negative number');
            }
            updateFollowUpEmailData(cardId, { followUpEmailDelayMinutes: minutes });
        } catch (error) {
            handleFollowUpError(error, 'follow-up-email-delay-minutes-update');
        }
    };

    const setNewLeadsEmail = (cardId, email) => {
        try {
            if (typeof email !== 'string') {
                throw new Error('New leads email must be a string');
            }
            // Basic email validation
            if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                throw new Error('Invalid email format for new leads email');
            }
            updateFollowUpEmailData(cardId, {
                toEmails: {
                    ...followUpEmailState[cardId]?.toEmails,
                    newLeadsEmail: email
                }
            });
        } catch (error) {
            handleFollowUpError(error, 'follow-up-email-new-leads-update');
        }
    };

    const setUserEmail = (cardId, email) => {
        try {
            if (typeof email !== 'string') {
                throw new Error('User email must be a string');
            }
            // Basic email validation
            if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                throw new Error('Invalid email format for user email');
            }
            updateFollowUpEmailData(cardId, {
                toEmails: {
                    ...followUpEmailState[cardId]?.toEmails,
                    userEmail: email
                }
            });
        } catch (error) {
            handleFollowUpError(error, 'follow-up-email-user-email-update');
        }
    };

    const setCcRecipients = (cardId, emails) => {
        try {
            if (typeof emails !== 'string') {
                throw new Error('CC recipients must be a string');
            }
            updateFollowUpEmailData(cardId, { ccRecipients: emails });
        } catch (error) {
            handleFollowUpError(error, 'follow-up-email-cc-update');
        }
    };

    const setBccRecipients = (cardId, emails) => {
        try {
            if (typeof emails !== 'string') {
                throw new Error('BCC recipients must be a string');
            }
            updateFollowUpEmailData(cardId, { bccRecipients: emails });
        } catch (error) {
            handleFollowUpError(error, 'follow-up-email-bcc-update');
        }
    };

    const setSubject = (cardId, subject) => {
        try {
            if (typeof subject !== 'string') {
                throw new Error('Subject must be a string');
            }
            updateFollowUpEmailData(cardId, { subject });
        } catch (error) {
            handleFollowUpError(error, 'follow-up-email-subject-update');
        }
    };

    const setMessage = (cardId, message) => {
        try {
            if (typeof message !== 'string') {
                throw new Error('Message must be a string');
            }
            updateFollowUpEmailData(cardId, { message });
        } catch (error) {
            handleFollowUpError(error, 'follow-up-email-message-update');
        }
    };

    const setAttachment = (cardId, attachment) => {
        try {
            updateFollowUpEmailData(cardId, { attachment });
        } catch (error) {
            handleFollowUpError(error, 'follow-up-email-attachment-update');
        }
    };

    // Add a setter for autoFollowUpIntroEmail with error handling
    const setAutoFollowUpIntroEmail = (cardId, isEnabled) => {
        try {
            if (typeof isEnabled !== 'boolean') {
                throw new Error('Auto follow-up intro email must be a boolean');
            }
            updateFollowUpEmailData(cardId, { autoFollowUpIntroEmail: isEnabled });
        } catch (error) {
            handleFollowUpError(error, 'follow-up-email-auto-toggle-update');
        }
    };

    return (
        <FollowUpEmailContext.Provider
            value={{
                followUpEmailState,
                error,
                initializeFollowUpEmail,
                clearError,
                // Expose setter functions
                setFollowUpEmailDelayHours,
                setFollowUpEmailDelayMinutes,
                setNewLeadsEmail,
                setUserEmail,
                setCcRecipients,
                setBccRecipients,
                setSubject,
                setMessage,
                setAttachment,
                setAutoFollowUpIntroEmail, // Expose the new setter
            }}
        >
            {children}
        </FollowUpEmailContext.Provider>
    );
};

// Custom hook to use the FollowUpEmailContext
export const useFollowUpEmailContext = () => {
    const context = useContext(FollowUpEmailContext);
    if (!context) {
        throw new Error('useFollowUpEmailContext must be used within a FollowUpEmailProvider');
    }
    return context;
};

// Custom hook to initialize follow-up email based on cardId
export const useInitializeFollowUpEmail = (cardId) => {
    const { initializeFollowUpEmail } = useFollowUpEmailContext();

    React.useEffect(() => {
        if (cardId) {
            initializeFollowUpEmail(cardId);
        }
    }, [cardId, initializeFollowUpEmail]);
};